// frontend/src/pages/SalesMachineAI.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { enviarMensagemElion } from '../services/elionService';
import PropertyCard from '../components/PropertyCard';
import {
  Sparkles,
  Send,
  ArrowRight,
  CornerDownRight,
  Zap,
  Search,
  Bot,
  AlertCircle,
  Building2,
  Tractor,
  Landmark,
  Warehouse,
} from 'lucide-react';

function pickArray(...candidates) {
  for (const c of candidates) {
    if (Array.isArray(c) && c.length) return c;
  }
  return [];
}

export default function SalesMachineAI() {
  const { usuario } = useAuth();

  const [query, setQuery] = useState('');
  const [conversation, setConversation] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ NOVO: mantém o mesmo chat
  const [chatId, setChatId] = useState(null);

  const scrollRef = useRef(null);

  // Auto-scroll
  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation, loading]);

  const normalizeResponse = (resp) => {
    // resp pode ser null
    const r = resp || {};

    // ✅ Texto: novo contrato (answerText) OU antigo (texto_explicativo)
    const texto =
      r.texto_explicativo ||
      r.answerText ||
      'Analisei sua base e encontrei estas oportunidades:';

    // ✅ Resultados: tenta achar em vários campos possíveis
    const resultados = pickArray(
      r.resultados,
      r.matches,
      r?.extractedPatch?.resultados,
      r?.extractedPatch?.matches,
      r?.statePatch?.resultados,
      r?.statePatch?.matches
    );

    // ✅ se vier em extractedPatch como "cards" ou outro nome no futuro
    const fallback = Array.isArray(r?.extractedPatch) ? r.extractedPatch : [];

    return {
      texto_explicativo: texto,
      resultados: resultados.length ? resultados : fallback,
      chatId: r.chatId || null,
    };
  };

  const handleSearch = async () => {
    if (!query.trim() || loading) return;

    // 1) UI Otimista
    const userMessage = { type: 'user', text: query };
    setConversation((prev) => [...prev, userMessage]);
    setQuery('');
    setLoading(true);

    try {
      // 2) Chama Backend (Callable chatElion)
      const raw = await enviarMensagemElion({
        mensagem: userMessage.text,
        modo: 'sales_machine',
        chatId, // ✅ mantém contexto
      });

      const response = normalizeResponse(raw);

      // ✅ atualiza chatId para próximas mensagens
      if (response.chatId) setChatId(response.chatId);

      // 3) Resposta da IA
      const aiMessage = {
        type: 'ai',
        text: response.texto_explicativo,
        data: Array.isArray(response.resultados) ? response.resultados : [],
      };

      setConversation((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error(error);
      setConversation((prev) => [
        ...prev,
        {
          type: 'error',
          text:
            error?.message || 'Tive um problema de conexão. Tente novamente.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const isInitialState = conversation.length === 0;

  // Sugestões estratégicas
  const sugestoesEstrategicas = [
    { icone: '🏠', texto: 'Troca: Casa por Apartamento de menor valor' },
    {
      icone: <Tractor size={16} />,
      texto: 'Fazenda de 500ha para lavoura (Investidor)',
    },
    {
      icone: <Building2 size={16} />,
      texto: 'Área para incorporação predial no centro',
    },
    {
      icone: <Landmark size={16} />,
      texto: 'Cliente com R$ 1M líquido para oportunidade',
    },
    {
      icone: <Warehouse size={16} />,
      texto: 'Galpão logístico já locado (Renda)',
    },
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] bg-gray-50 text-slate-800 overflow-hidden font-sans relative">
      {/* ================= ÁREA DE CONTEÚDO (FEED) ================= */}
      <div
        className={`flex-1 overflow-y-auto p-4 md:p-8 space-y-8 no-scrollbar scroll-smooth z-10 ${
          isInitialState ? 'flex items-center justify-center' : ''
        }`}
      >
        {/* ESTADO ZERO */}
        {isInitialState && (
          <div className="flex flex-col items-center justify-center w-full max-w-4xl animate-fadeIn px-4 mt-[-5vh]">
            <div className="mb-6 relative">
              <div className="bg-blue-50 p-5 rounded-2xl border border-blue-100 shadow-sm">
                <Sparkles size={40} className="text-blue-600 fill-blue-50/50" />
              </div>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-center mb-3 text-slate-800">
              Olá,{' '}
              <span className="text-blue-600">
                {usuario?.nome?.split(' ')[0] || 'Parceiro'}
              </span>
            </h1>
            <p className="text-lg text-slate-500 text-center max-w-lg leading-relaxed">
              Descreva o negócio. Eu cruzo nossa base para encontrar fechamentos
              diretos e triangulações.
            </p>

            <div className="w-full mt-8 relative max-w-2xl shadow-xl rounded-2xl">
              <div className="relative flex items-center bg-white rounded-2xl p-2 border border-gray-200 focus-within:border-blue-500/50 focus-within:ring-4 focus-within:ring-blue-500/10 transition-all">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Ex: Cliente busca casa de 1M e dá terreno no Alphaville..."
                  className="w-full bg-transparent text-slate-800 px-4 py-3 text-lg outline-none placeholder-slate-400"
                  autoFocus
                />
                <button
                  onClick={handleSearch}
                  disabled={!query.trim() || loading}
                  className="p-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                >
                  <ArrowRight size={20} />
                </button>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap justify-center gap-3">
              {sugestoesEstrategicas.map((sugestao, i) => (
                <button
                  key={i}
                  onClick={() =>
                    setQuery(
                      typeof sugestao.icone === 'string'
                        ? `${sugestao.icone} ${sugestao.texto}`
                        : sugestao.texto
                    )
                  }
                  className="px-4 py-2.5 rounded-xl bg-white border border-gray-200 text-slate-600 text-sm font-medium hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50/50 transition-all shadow-sm flex items-center gap-2"
                >
                  <span className="text-blue-500">{sugestao.icone}</span>
                  {sugestao.texto}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* FEED DE CONVERSA */}
        {!isInitialState && (
          <div className="w-full max-w-6xl mx-auto pb-32">
            {conversation.map((msg, idx) => (
              <div key={idx} className="mb-10 animate-fadeIn">
                {/* USUÁRIO */}
                {msg.type === 'user' && (
                  <div className="flex justify-end mb-6">
                    <div className="bg-white text-slate-800 px-6 py-4 rounded-3xl rounded-tr-sm text-lg max-w-2xl border border-gray-200 shadow-sm">
                      {msg.text}
                    </div>
                  </div>
                )}

                {/* ERRO */}
                {msg.type === 'error' && (
                  <div className="flex justify-center mb-6">
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm flex items-center gap-2">
                      <AlertCircle size={16} /> {msg.text}
                    </div>
                  </div>
                )}

                {/* IA */}
                {msg.type === 'ai' && (
                  <div className="flex flex-col gap-6">
                    <div className="flex gap-4 items-start">
                      <div className="min-w-[36px] h-[36px] rounded-full bg-white flex items-center justify-center border border-blue-100 shadow-sm mt-1">
                        <Sparkles className="text-blue-600" size={18} />
                      </div>
                      <div className="text-lg text-slate-700 leading-relaxed pt-1">
                        {msg.text}
                      </div>
                    </div>

                    {/* RESULTADOS */}
                    {Array.isArray(msg.data) && msg.data.length > 0 && (
                      <div className="pl-0 md:pl-14 grid gap-8 mt-2">
                        {msg.data.map((match, mIdx) => {
                          const tipo =
                            match?.tipo || match?.type || 'TRIANGULAR';
                          const score = match?.score ?? match?.pontuacao ?? 0;
                          const razao = match?.razao || match?.reason || '';
                          const saldos = Array.isArray(match?.saldos)
                            ? match.saldos
                            : [];
                          const imoveis = Array.isArray(match?.imoveis)
                            ? match.imoveis
                            : [];

                          return (
                            <div
                              key={mIdx}
                              className="bg-white border border-gray-200 rounded-3xl p-6 hover:shadow-lg transition-all shadow-sm group relative overflow-hidden"
                            >
                              <div className="relative flex flex-col sm:flex-row sm:items-center justify-between mb-6 pb-4 border-b border-gray-100 gap-3">
                                <div className="flex items-center gap-3">
                                  <span
                                    className={`text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider flex items-center gap-1.5 ${
                                      String(tipo).toUpperCase() === 'DIRETO'
                                        ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                                        : 'bg-blue-100 text-blue-700 border border-blue-200'
                                    }`}
                                  >
                                    {String(tipo).toUpperCase() === 'DIRETO' ? (
                                      <Zap size={14} />
                                    ) : (
                                      <Search size={14} />
                                    )}
                                    {String(tipo).toUpperCase() === 'DIRETO'
                                      ? 'Fechamento Direto'
                                      : 'Triangulação'}
                                  </span>

                                  <span className="text-xs text-slate-500 font-mono bg-gray-100 px-2 py-1 rounded border border-gray-200">
                                    Match: {Number(score) || 0}%
                                  </span>
                                </div>
                                {razao ? (
                                  <div className="text-sm text-slate-500 italic">
                                    "{razao}"
                                  </div>
                                ) : null}
                              </div>

                              <div className="relative flex flex-col lg:flex-row items-center gap-2 overflow-x-auto pb-4 scrollbar-hide">
                                {imoveis.map((imovel, iIdx) => (
                                  <React.Fragment key={imovel?.id || iIdx}>
                                    <div className="w-full lg:w-[300px] flex-shrink-0 relative transform transition-transform duration-300">
                                      <div className="rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow hover:border-blue-300">
                                        <PropertyCard
                                          property={imovel}
                                          compact={true}
                                        />
                                      </div>
                                    </div>

                                    {iIdx < imoveis.length - 1 && (
                                      <div className="flex flex-col items-center justify-center min-w-[60px] text-slate-400 px-2 py-4 lg:py-0 relative z-10">
                                        <div className="bg-blue-50 p-2 rounded-full border border-blue-200 shadow-sm">
                                          <ArrowRight
                                            size={20}
                                            className="hidden lg:block text-blue-600"
                                          />
                                          <CornerDownRight
                                            size={20}
                                            className="block lg:hidden text-blue-600"
                                          />
                                        </div>

                                        {saldos && saldos[iIdx] > 0 && (
                                          <span className="absolute -top-1 lg:top-auto lg:-mt-10 text-[10px] font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200 whitespace-nowrap shadow-sm">
                                            + R${' '}
                                            {(saldos[iIdx] / 1000).toFixed(0)}k
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </React.Fragment>
                                ))}
                              </div>

                              <div className="mt-4 flex justify-end relative pt-2">
                                <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-xl font-medium text-sm flex items-center gap-2 shadow-md shadow-blue-100 transition-all transform hover:translate-y-[-1px]">
                                  Iniciar Negociação <ArrowRight size={16} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Loading */}
            {loading && (
              <div className="flex items-center gap-4 pl-0 md:pl-14 animate-pulse">
                <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center border border-blue-200 shadow-sm">
                  <Bot size={18} className="text-blue-600" />
                </div>
                <div className="flex space-x-1.5">
                  <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                  <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                  <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce"></div>
                </div>
              </div>
            )}

            <div ref={scrollRef} className="h-4" />
          </div>
        )}
      </div>

      {/* ================= BARRA DE INPUT FIXA ================= */}
      {!isInitialState && (
        <div className="absolute bottom-0 left-0 w-full p-4 md:p-6 bg-white border-t border-gray-200 relative z-20 shadow-[0_-10px_30px_rgba(0,0,0,0.03)]">
          <div className="max-w-4xl mx-auto relative group">
            <div className="relative flex items-center bg-gray-50 rounded-2xl border border-gray-200 focus-within:border-blue-500/50 focus-within:ring-4 focus-within:ring-blue-500/10 transition-all p-2">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Continue a conversa..."
                className="w-full bg-transparent text-slate-800 pl-4 pr-14 py-3 text-lg outline-none placeholder-slate-400"
                autoFocus
                disabled={loading}
              />
              <button
                onClick={handleSearch}
                disabled={loading || !query.trim()}
                className="p-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-xl transition-all shadow-sm"
              >
                <Send size={20} />
              </button>
            </div>
            <div className="text-center mt-2 text-[10px] text-slate-400 font-medium">
              A Elion AI pode cometer erros. Confirme os valores.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
